const prefix = 'experiment-visit:';
const pending = new Map();
const sending = new Set();
const acknowledged = new Set();
const persisted = new WeakSet();
let retryTimer = null;

function storage(storageName, method, ...args) {
    try {
        return window[storageName][method](...args);
    } catch (error) {
        // The memory queue still works when browser storage is unavailable.
        return null;
    }
}

function setVisitorCookie(visitorId) {
    if (!visitorId || document.cookie.split(';').some((cookie) => cookie.trim().startsWith('experiment_visitor_id='))) {
        return;
    }

    const parts = window.location.hostname.split('.');
    let domain = parts.slice(-2).join('.');

    if (domain.startsWith('com.') || domain.startsWith('co.')) {
        domain = parts.slice(-3).join('.');
    }

    document.cookie = 'experiment_visitor_id=' + encodeURIComponent(visitorId) + '; max-age=31536000; path=/; domain=.' + domain;
}

async function send(key, entry) {
    sending.add(key);

    try {
        if (window.navigator?.locks) {
            await window.navigator.locks.request(key, async () => {
                // Another tab may have acknowledged the visit while we waited.
                if (persisted.has(entry)) {
                    try {
                        if (window.localStorage.getItem(key) === null) {
                            pending.delete(key);
                            return;
                        }
                    } catch (error) {
                        // Keep the memory queue usable if storage becomes unavailable.
                    }
                }

                await deliver(key, entry);
            });
        } else {
            await deliver(key, entry);
        }
    } catch (error) {
        // Some browser contexts expose Web Locks but reject access to them.
        await deliver(key, entry);
    } finally {
        sending.delete(key);
    }
}

async function deliver(key, entry) {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeout = controller ? window.setTimeout(() => controller.abort(), 10000) : null;

    try {
        const response = await window.fetch(entry.url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(entry.payload),
            keepalive: true,
            ...(controller ? { signal: controller.signal } : {}),
        });

        const result = await response.json();

        if (!response.ok || (!result.stored && !result.skipped)) {
            throw new Error('Visit was not acknowledged');
        }

        pending.delete(key);
        storage('localStorage', 'removeItem', key);

        if (result.stored) {
            acknowledged.add(key);
            storage('sessionStorage', 'setItem', key, '1');
            setVisitorCookie(result.visitor_id);
        }
    } catch (error) {
        if (retryTimer === null) {
            retryTimer = window.setTimeout(() => {
                retryTimer = null;
                flushVisits();
            }, 10000);
        }
    } finally {
        window.clearTimeout(timeout);
    }
}

export function flushVisits() {
    for (const [key, entry] of pending) {
        if (!sending.has(key)) {
            send(key, entry);
        }
    }
}

export function queueVisit(baseUrl, eventName, eventData = {}) {
    const journey = eventData.quote_id || Date.now() + ':' + Math.random();
    const key = prefix + encodeURIComponent(eventName + ':' + journey);

    if (acknowledged.has(key) || storage('sessionStorage', 'getItem', key) || pending.has(key)) {
        return;
    }

    const entry = {
        url: baseUrl.replace(/\/?$/, '/') + 'ab/add-visit-and-event',
        payload: {
            event_name: eventName,
            quote_id: eventData.quote_id,
            event_data: { ...eventData, occurred_at: new Date().toISOString() },
        },
    };

    pending.set(key, entry);

    if (storage('localStorage', 'setItem', key, JSON.stringify(entry)) !== null) {
        persisted.add(entry);
    }
    flushVisits();
}

function restore(key, value) {
    try {
        const entry = JSON.parse(value);

        if (entry?.url && entry.payload?.event_name && entry.payload?.event_data) {
            persisted.add(entry);
            pending.set(key, entry);
        }
    } catch (error) {
        // An invalid entry must not prevent delivery of other visits.
    }
}

try {
    for (let index = 0; index < window.localStorage.length; index++) {
        const key = window.localStorage.key(index);

        if (key?.startsWith(prefix)) {
            restore(key, window.localStorage.getItem(key));
        }
    }
} catch (error) {
    // Sending still works when browser storage is unavailable.
}

window.addEventListener('online', flushVisits);
window.addEventListener('pageshow', flushVisits);
window.addEventListener('pagehide', flushVisits);
window.addEventListener('storage', (event) => {
    if (event.key?.startsWith(prefix)) {
        if (event.newValue === null) {
            pending.delete(event.key);
        } else {
            restore(event.key, event.newValue);
        }

        flushVisits();
    }
});

document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
        flushVisits();
    }
});

flushVisits();
